import type { Query } from "mongoose";
import type { z } from "zod";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import { DtoValidatorKeys } from "../validatableClass.js";

export enum PositionDepthSource {
    DEPTH_FIELD = "DEPTH_FIELD",
    POSITION_DEPTH = "POSITION_DEPTH",
}

type PointDocument = {
    latitude?: number;
    longitude?: number;
    depth?: number;
    position?: VertexInfo;
};

const POSITION_PATH = "position";

export function requiredUnlessPositioned(this: PointDocument): boolean {
    return typeof this.position?.lat !== "number" || typeof this.position?.lng !== "number";
}

const CREATE_VARIANTS = new Set([DtoValidatorKeys.Create, DtoValidatorKeys.CreateLax]);
const REQUEST_VARIANTS = new Set([...CREATE_VARIANTS, DtoValidatorKeys.Update]);

export function requireRequestPositionLatLng(
    value: { latitude?: number; longitude?: number; position?: { lat?: number | null; lng?: number | null } },
    ctx: z.RefinementCtx,
    variant: DtoValidatorKeys,
): void {
    if (!REQUEST_VARIANTS.has(variant) || !value) return;
    if (!value.position) {
        if (!CREATE_VARIANTS.has(variant)) return;
        (["latitude", "longitude"] as const)
            .filter((key) => value[key] === undefined)
            .forEach((key) => ctx.addIssue({ code: "custom", path: [key], message: "Required" }));
        return;
    }
    (["lat", "lng"] as const)
        .filter((key) => typeof value.position[key] !== "number")
        .forEach((key) => ctx.addIssue({ code: "custom", path: ["position", key], message: "Required" }));
}

const isPositionPath = (path: string) => path === POSITION_PATH || path.startsWith(`${POSITION_PATH}.`);

function writesPosition(update: Record<string, unknown>): boolean {
    return Object.entries(update).some(([key, value]) =>
        key.startsWith("$")
            ? value != null && typeof value === "object" && Object.keys(value).some(isPositionPath)
            : isPositionPath(key),
    );
}

export const addPositionSyncSaveHook = (depthSource: PositionDepthSource): ["save", (this: PointDocument) => void] => [
    "save",
    function (this: PointDocument) {
        if (typeof this.latitude !== "number" || typeof this.longitude !== "number") return;
        const depth = (depthSource === PositionDepthSource.DEPTH_FIELD ? this.depth : this.position?.depth) ?? 0;
        if (
            this.position?.lat === this.latitude &&
            this.position?.lng === this.longitude &&
            this.position?.depth === depth
        ) {
            return;
        }
        this.position = new VertexInfo({ lat: this.latitude, lng: this.longitude, depth });
    },
];

export const addPositionSyncUpdateHook = (
    depthSource: PositionDepthSource,
): [["updateOne", "findOneAndUpdate", "updateMany"], (this: Query<unknown, unknown>) => void] => [
    ["updateOne", "findOneAndUpdate", "updateMany"],
    function (this: Query<unknown, unknown>) {
        const update = this.getUpdate();
        if (update == null || Array.isArray(update)) return;
        // Mongo rejects an update that writes both a path and its parent or child.
        if (writesPosition(update)) return;
        const set = (update.$set ?? {}) as Record<string, unknown>;
        const fieldToPositionPath: [string, string][] = [
            ["latitude", "position.lat"],
            ["longitude", "position.lng"],
            ...(depthSource === PositionDepthSource.DEPTH_FIELD
                ? [["depth", "position.depth"] as [string, string]]
                : []),
        ];
        const positionSet = Object.fromEntries(
            fieldToPositionPath
                .map(([field, positionPath]) => [positionPath, set[field] ?? update[field]])
                .filter(([, value]) => value != null),
        );
        if (Object.keys(positionSet).length === 0) return;
        this.setUpdate({ ...update, $set: { ...set, ...positionSet } });
    },
];
